// src/pages/Vouchers/RedeemVoucherPage.js
//
// Redeem voucher/package page.
// ===========================
//
// RESPONSIBILITIES
// ----------------
// This page creates a NEW redeem entry for an existing voucher/package.
//
// Current shared domain behavior remains unchanged:
//   - redeemVoucher(...)
//   - archiveVoucher(...)
//   - remaining balance logic
//   - noted flag support
//   - redeem comment support
//
// NEW WORDING UPDATE
// ------------------
// The page now follows the same business wording convention as the rest of the
// updated voucher/package UI:
//
//   - category !== "Pakiet"  -> "Voucher"
//   - category === "Pakiet"  -> "Package"
//
// IMPORTANT:
// ----------
// This is a UI wording split, not a redeem-domain split.
// Technically the flow stays shared and fully compatible with the existing
// backend voucher service methods.
//
// UX GOALS PRESERVED
// ------------------
// - Show voucher/package summary before redemption.
// - Validate amount client-side for quick feedback.
// - Keep backend as the final source of truth.
// - Auto-archive when remaining balance becomes zero.
// - Navigate back to overview after successful redemption.
//

import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { redeemVoucher, archiveVoucher } from "../../services/voucherService";
import "./RedeemVoucherPage.css";

// -----------------------------------------------------------------------------
// Formatting helpers
// -----------------------------------------------------------------------------

/**
 * Format a monetary value in a simple fixed 2-decimal representation.
 *
 * IMPORTANT:
 * We intentionally keep this lightweight and aligned with the rest of the
 * backoffice screens rather than introducing locale formatting differences.
 */
function fmtMoney(value) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue.toFixed(2) : "0.00";
}

/**
 * Format a date-ish value for UI display.
 */
function fmtDate(dateValue) {
  if (!dateValue) return "—";

  const d = new Date(dateValue);
  return Number.isNaN(d.getTime()) ? String(dateValue) : d.toLocaleDateString();
}

// -----------------------------------------------------------------------------
// Business wording helpers
// -----------------------------------------------------------------------------

const PACKAGE_CATEGORY_VALUE = "Pakiet";

/**
 * getBusinessModeFromVoucherRow(voucher)
 *
 * Shared wording rule:
 *   category === "Pakiet" -> package mode
 *   otherwise             -> voucher mode
 */
function getBusinessModeFromVoucherRow(voucher) {
  return voucher?.category === PACKAGE_CATEGORY_VALUE ? "package" : "voucher";
}

/**
 * getEntityUiText(voucher)
 *
 * Centralizes all operator-facing wording for this page.
 *
 * This keeps JSX readable and prevents repeated package/voucher checks.
 */
function getEntityUiText(voucher, t) {
  const mode = getBusinessModeFromVoucherRow(voucher);
  const isPackage = mode === "package";

  const entityKey = isPackage ? "package" : "voucher";
  const entityLabel = t(`vouchers:entity.${entityKey}.singular`, {
    defaultValue: isPackage ? "Package" : "Voucher",
  });
  const entityLower = t(`vouchers:entity.${entityKey}.lowerSingular`, {
    defaultValue: entityLabel.toLowerCase(),
  });

  const i18nVars = { entity: entityLabel, entityLower };

  return {
    entityLabel,
    entityLower,

    pageTitle: t("vouchers:redeem.pageTitle", i18nVars),

    missingEntityError: t("vouchers:redeem.missingEntityError", i18nVars),
    backButton: t("common:actions.back"),

    entityNumberLabel: t("vouchers:redeem.entityNumberLabel", i18nVars),
    totalValueLabel: t("vouchers:redeem.totalValueLabel"),
    remainingBalanceLabel: t("vouchers:redeem.remainingBalanceLabel"),
    expirationLabel: t("vouchers:redeem.expirationLabel"),
    summaryAriaLabel: t("vouchers:redeem.summaryAriaLabel", i18nVars),
    formAriaLabel: t("vouchers:redeem.formAriaLabel", i18nVars),

    reservationIdLabel: t("vouchers:redeem.reservationIdLabel"),
    reservationIdPlaceholder: t("vouchers:redeem.reservationIdPlaceholder"),

    redeemAmountLabel: t("vouchers:redeem.redeemAmountLabel"),

    notedToggleLabel: t("vouchers:redeem.notedToggleLabel"),
    notedHint: t("vouchers:redeem.notedHint", i18nVars),

    commentLabel: t("vouchers:redeem.commentLabel"),
    commentPlaceholder: t("vouchers:redeem.commentPlaceholder", i18nVars),
    commentHint: t("vouchers:redeem.commentHint"),

    invalidAmountError: t("vouchers:redeem.invalidAmountError"),
    commentTooLongError: (limit) =>
      t("vouchers:redeem.commentTooLongError", { limit }),
    exceedsBalanceError: (remaining) =>
      t("vouchers:redeem.exceedsBalanceError", {
        remaining: fmtMoney(remaining),
      }),

    redeemSuccess: t("vouchers:redeem.redeemSuccess", i18nVars),
    fullyRedeemedArchiving: t("vouchers:redeem.fullyRedeemedArchiving", i18nVars),
    fullyRedeemedArchived: t("vouchers:redeem.fullyRedeemedArchived", i18nVars),
    redeemError: t("vouchers:redeem.redeemError", i18nVars),

    submitButton: t("vouchers:redeem.submitButton", i18nVars),
    submittingButton: t("vouchers:redeem.submittingButton"),
    cancelButton: t("common:actions.cancel"),
  };
}

export default function RedeemVoucherPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(["common", "vouchers"]);
  // i18n note:
  // The redeem flow remains technically shared for vouchers and packages.
  // Only operator-facing wording is translated and business-mode aware.

  const { state } = location;
  const voucher = state?.voucher;

  const backToOverviewPath =
  state?.returnTo || "/vouchers/overview";

  // ---------------------------------------------------------------------------
  // Business wording derived from voucher row
  // ---------------------------------------------------------------------------
  const uiText = useMemo(() => getEntityUiText(voucher, t), [voucher, t]);

  // ---------------------------------------------------------------------------
  // Local form state
  // ---------------------------------------------------------------------------
  const [reservationId, setReservationId] = useState("");
  const [redeemedAmount, setRedeemedAmount] = useState("");

  // Default noted flag:
  // - start from voucher.to_note
  // - backend also supports defaulting if omitted
  //
  // We still expose it explicitly in the form so the operator can override it.
  const initialNoted = useMemo(() => Boolean(voucher?.to_note), [voucher]);
  const [noted, setNoted] = useState(initialNoted);

  // Comment saved to redeem_vouchers.comment
  const [comment, setComment] = useState("");
  const COMMENT_MAX = 500;

  // ---------------------------------------------------------------------------
  // UX state
  // ---------------------------------------------------------------------------
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [clampNotice, setClampNotice] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // ---------------------------------------------------------------------------
  // Guard: navigation without voucher/package payload
  // ---------------------------------------------------------------------------
  //
  // This page expects the caller to pass `state.voucher`.
  // If not present, we fail gracefully.
  //
  if (!voucher) {
    return (
      <div
        className="redeem-voucher-page"
        role="main"
        aria-label={uiText.pageTitle}
      >
        <h1>{uiText.pageTitle}</h1>
        <div className="message error">{uiText.missingEntityError}</div>
        <button onClick={() => navigate(backToOverviewPath)}>
          {uiText.backButton}
        </button>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Submit handler
  // ---------------------------------------------------------------------------
  //
  // Shared redeem flow:
  //   1) validate locally
  //   2) call redeemVoucher(...)
  //   3) if remaining becomes 0 -> archiveVoucher(...)
  //   4) navigate back to overview
  //
  const handleRedeemSubmit = async () => {
    const amountNumber = Number(redeemedAmount);

    setErrorMsg("");
    setMessage("");
    setClampNotice("");

    if (!Number.isFinite(amountNumber) || amountNumber <= 0) {
      setErrorMsg(uiText.invalidAmountError);
      return;
    }

    if (comment.length > COMMENT_MAX) {
      setErrorMsg(uiText.commentTooLongError(COMMENT_MAX));
      return;
    }

    // Optional UX guard:
    // Fast client-side check before the backend roundtrip.
    // Backend remains authoritative and validates again.
    const localRemaining = Number(voucher.remaining_balance);
    if (Number.isFinite(localRemaining) && amountNumber > localRemaining) {
      setErrorMsg(uiText.exceedsBalanceError(localRemaining));
      return;
    }

    setLoading(true);

    try {
      // redeemVoucher(voucherId, reservationId, redeemedAmount, noted?, comment?)
      const response = await redeemVoucher(
        voucher.id,
        reservationId || undefined,
        amountNumber,
        noted,
        comment.trim() || undefined
      );

      const remaining = Number(
        response?.remaining_balance ?? voucher.remaining_balance
      );

      if (remaining === 0) {
        setMessage(uiText.fullyRedeemedArchiving);
        await archiveVoucher(voucher.id);
        setMessage(uiText.fullyRedeemedArchived);
      } else {
        setMessage(response?.message || uiText.redeemSuccess);
      }

      setTimeout(() => navigate(backToOverviewPath), 1200);
    } catch (err) {
      const apiMessage =
        err?.response?.data?.message ||
        err?.message ||
        uiText.redeemError;

      setErrorMsg(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="redeem-voucher-page"
      role="main"
      aria-label={uiText.pageTitle}
      data-mode={getBusinessModeFromVoucherRow(voucher)}
    >
      <h1>{uiText.pageTitle}</h1>

      {/* ---------------------------------------------------------------------
         Voucher/package summary
         ---------------------------------------------------------------------
         The redeem flow is operationally safer when the operator can see:
           - number
           - total value
           - remaining balance
           - expiration
      */}
      <div
        className="voucher-summary"
        role="region"
        aria-label={uiText.summaryAriaLabel}
      >
        <div className="summary-row">
          <span className="label">{uiText.entityNumberLabel}</span>
          <span>{voucher.voucher_no}</span>
        </div>

        <div className="summary-row">
          <span className="label">{uiText.totalValueLabel}</span>
          <span>{fmtMoney(voucher.value)} PLN</span>
        </div>

        <div className="summary-row">
          <span className="label">{uiText.remainingBalanceLabel}</span>
          <span>{fmtMoney(voucher.remaining_balance)} PLN</span>
        </div>

        <div className="summary-row">
          <span className="label">{uiText.expirationLabel}</span>
          <span>{fmtDate(voucher.expiration_date)}</span>
        </div>
      </div>

      {/* ---------------------------------------------------------------------
         Redeem form
         ---------------------------------------------------------------------
         Shared for voucher + package. Only wording differs.
      */}
      <div className="redeem-form" role="form" aria-label={uiText.formAriaLabel}>
        <label htmlFor="reservationId">{uiText.reservationIdLabel}</label>
        <input
          id="reservationId"
          type="text"
          value={reservationId}
          onChange={(e) => setReservationId(e.target.value)}
          placeholder={uiText.reservationIdPlaceholder}
          disabled={loading}
          autoComplete="off"
        />

        <label htmlFor="redeemAmount">{uiText.redeemAmountLabel}</label>
        <input
          id="redeemAmount"
          type="number"
          value={redeemedAmount}
          onChange={(e) => setRedeemedAmount(e.target.value)}
          placeholder={uiText.redeemAmountLabel}
          step="0.01"
          min="0.01"
          disabled={loading}
          inputMode="decimal"
        />

        {/* Noted toggle */}
        <div className="noted-toggle">
          <label>
            <input
              type="checkbox"
              checked={noted}
              onChange={(e) => setNoted(e.target.checked)}
              disabled={loading}
            />
            &nbsp;<strong>{uiText.notedToggleLabel}</strong>
          </label>

          <p className="hint">{uiText.notedHint}</p>
        </div>

        {/* Comment */}
        <label htmlFor="redeemComment">{uiText.commentLabel}</label>
        <textarea
          id="redeemComment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={uiText.commentPlaceholder}
          maxLength={COMMENT_MAX}
          rows={4}
          disabled={loading}
        />

        <div className="comment-meta" aria-live="polite">
          <span className="comment-hint">{uiText.commentHint}</span>
          <span className="comment-count">
            {comment.length}/{COMMENT_MAX}
          </span>
        </div>

        {errorMsg && (
          <div className="message error" role="alert">
            {errorMsg}
          </div>
        )}

        {clampNotice && <div className="message clamp">{clampNotice}</div>}
        {message && <div className="message info">{message}</div>}

        <div className="actions">
          <button onClick={handleRedeemSubmit} disabled={loading}>
            {loading ? uiText.submittingButton : uiText.submitButton}
          </button>

          <button
            onClick={() => navigate(backToOverviewPath)}
            disabled={loading}
          >
            {uiText.cancelButton}
          </button>
        </div>
      </div>
    </div>
  );
}